<template>
    <custom-table
        :title="$t('pages.transaction.title')"
        :subTitle="$t('pages.transaction.subTitle')"
        rowKey="id"
        :items="table.data"
        :loading="table.loading"
        selectableRows
        :columns="fields"
        :actions="actions"
        :pagination="table.pagination"
        :sortMeta="table.pagination.sort || []"
        changeUrlPage
        filterComponentName="TransactionFilter"
        @action="handleClickAction"
        @selectableRows="handleSelectedRow"
        @changeTable="handleTableChange"
        @sort="handleSort"
    >
        <template v-slot:direction="{ row: record }">
            <span class="tx-direction" :class="record.direction_id === 1 ? 'tx-direction--in' : 'tx-direction--out'">
                <svg v-if="record.direction_id === 1" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="6 9 12 15 18 9"/>
                </svg>
                <svg v-else width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="18 15 12 9 6 15"/>
                </svg>
                {{ $t('common.transaction.direction.' + record.direction_id) }}
            </span>
        </template>

        <template v-slot:source="{ row: record }">
            <div v-if="record.source_type" class="tx-source">
                <span class="tx-source-label">{{ sourceLabel(record.source_type) }}</span>
                <span class="tx-source-id">#{{ record.source_id }}</span>
            </div>
            <span v-else class="tx-badge tx-badge--manual">{{ $t('common.transaction.badge.manualPill') }}</span>
        </template>

        <template v-slot:target="{ row: record }">
            <span v-if="record.target_type" class="tx-target">
                {{ targetLabel(record.target_type) }}<span v-if="record.target_id"> #{{ record.target_id }}</span>
            </span>
            <span v-else class="tx-muted">{{ $t('pages.transaction.target.noTarget') }}</span>
        </template>

        <template v-slot:amounts="{ row: record }">
            <div class="tx-amounts">
                <div class="tx-amounts-total">{{ formatTRY(record.total_amount) }}</div>
                <div class="tx-amounts-line2">
                    <span class="tx-amounts-paid">{{ formatTRY(record.paid_amount) }}</span>
                    <span class="tx-amounts-sep">/</span>
                    <span class="tx-amounts-remaining">{{ formatTRY(record.remaining_amount) }}</span>
                </div>
            </div>
        </template>

        <template v-slot:status="{ row: record }">
            <span class="tx-pill" :class="'tx-pill--' + record.status_id">
                {{ $t('common.transaction.status.' + record.status_id) }}
            </span>
        </template>

        <template v-slot:created_at="{ row: record }">
            {{ formatDate(record.created_at) }}
        </template>

        <template v-slot:actions="{ row: record }">
            <div class="d-flex justify-content-end">
                <el-tooltip :content="$t('common.transaction.actions.openPayments')" placement="top">
                    <button class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1" @click="openPayments(record)">
                        <span class="svg-icon svg-icon-3">
                            <inline-svg src="/media/icons/duotune/finance/fin010.svg"/>
                        </span>
                    </button>
                </el-tooltip>
                <el-tooltip v-if="!record.source_type" :content="$t('common.delete')" placement="top">
                    <el-popconfirm :title="$t('messages.sureDelete')" :confirm-button-text="$t('btn.yes')" :cancel-button-text="$t('btn.no')" @confirm="deleteRecord([record.id])">
                        <template #reference>
                            <a class="btn btn-icon btn-bg-light btn-active-color-primary btn-sm">
                                <span class="svg-icon svg-icon-3">
                                    <inline-svg src="/media/icons/duotune/general/gen027.svg"/>
                                </span>
                            </a>
                        </template>
                    </el-popconfirm>
                </el-tooltip>
            </div>
        </template>
    </custom-table>

    <ManualTransactionDialog ref="manualDialog" @created="onCreated"/>
</template>

<script>
import CustomTable from "@/components/custom-table";
import transactionService from "@/components/payment/transactionService";
import { resolveSourceLabelKey } from "@/core/services/sourceResolver";
import ManualTransactionDialog from "@/components/payment/ManualTransactionDialog.vue";

export default {
    name: "transaction-index",
    components: {
        CustomTable,
        ManualTransactionDialog,
    },
    data() {
        return {
            fields: [
                { name: this.$t("pages.transaction.cols.id"), key: "id", class: '', sortable: true },
                { name: this.$t("pages.transaction.cols.direction"), sortingField: "direction_id", sortable: true, scopedSlots: { customRender: "direction" } },
                { name: this.$t("pages.transaction.cols.source"), sortingField: "source_type", sortable: true, scopedSlots: { customRender: "source" } },
                { name: this.$t("pages.transaction.cols.target"), scopedSlots: { customRender: "target" } },
                { name: this.$t("pages.transaction.cols.amounts"), sortingField: "total_amount", sortable: true, scopedSlots: { customRender: "amounts" } },
                { name: this.$t("pages.transaction.cols.status"), sortingField: "status_id", sortable: true, scopedSlots: { customRender: "status" } },
                { name: this.$t("pages.transaction.cols.createdAt"), sortingField: "created_at", sortable: true, scopedSlots: { customRender: "created_at" } },
                { name: this.$t("common.action"), key: "action", scopedSlots: { customRender: "actions" } },
            ],
            actions: [
                { name: "new", icon: "bi-plus-lg", label: "btn.add" },
                { name: "refresh", icon: "bi-arrow-repeat", label: "btn.refresh" },
                { name: "delete", icon: "bi-trash", label: "btn.delete" },
            ],
            selectedRowKeys: [],
            filterParameters: {},
        }
    },
    computed: {
        table() {
            return this.$store.state.transaction.table;
        },
    },
    mounted() {
        this.setCurrentPageBreadcrumbs(this.$t("pages.transaction.title"), []);
        this.filterParameters = { sort: 'id:desc' };
        this.refreshTable();
        transactionService.ensurePaymentMethods();
        transactionService.ensureCashRegisters();
    },
    methods: {
        formatTRY(amount) {
            const n = parseFloat(amount);
            if (isNaN(n)) return '-';
            return n.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 });
        },
        formatDate(d) {
            if (!d) return '-';
            const dt = new Date(d);
            if (isNaN(dt.getTime())) return d;
            return dt.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })
                + ' · ' + dt.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
        },
        sourceLabel(type) {
            const key = resolveSourceLabelKey(type) || ('common.transaction.sourceModules.' + type);
            const t = this.$t(key);
            return t === key ? type : t;
        },
        targetLabel(type) {
            const key = 'pages.transaction.target.' + type;
            const t = this.$t(key);
            return t === key ? type : t;
        },
        openPayments(record) {
            transactionService.open({
                transactionId: record.id,
                onChange: () => this.refreshTable(),
            });
        },
        openManualCreate() {
            this.$refs.manualDialog?.open();
        },
        onCreated() {
            this.refreshTable();
        },
        handleClickAction(name) {
            switch (name) {
                case "refresh":
                    this.refreshTable();
                    break;
                case "new":
                    this.openManualCreate();
                    break;
                case "delete":
                    this.deleteRecord(this.selectedRowKeys.flat());
                    break;
                default:
                    break;
            }
        },
        handleTableChange(pagination, filterData) {
            const currentSort = this.filterParameters.sort || 'id:desc';
            this.filterParameters = { ...filterData, sort: currentSort };
            this.$store.dispatch('transaction/get', {
                page: pagination,
                filterData: this.filterParameters,
            });
        },
        handleSort({ columnName, order }) {
            this.filterParameters = {
                ...this.filterParameters,
                sort: `${columnName}:${order}`,
            };
            this.$store.dispatch('transaction/get', {
                page: { current: this.$route.query.page },
                filterData: this.filterParameters,
            });
        },
        handleSelectedRow(record) {
            this.selectedRowKeys = record;
        },
        deleteRecord(id) {
            this.$store.dispatch('transaction/delete', { id }).then(() => this.refreshTable());
        },
        refreshTable() {
            this.$store.dispatch('transaction/get', {
                page: { current: this.$route.query.page },
                filterData: Object.assign(this.filterParameters, this.$root.getFilterWithUrl({})),
            });
        },
    },
}
</script>

<style scoped>
.tx-direction {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 9px;
    border-radius: 999px;
    font-size: 11.5px;
    font-weight: 600;
}

.tx-direction--in {
    background: rgba(40, 167, 69, 0.12);
    color: #28a745;
}

.tx-direction--out {
    background: rgba(239, 68, 68, 0.12);
    color: #ef4444;
}

html.dark .tx-direction--in,
html.midnight .tx-direction--in { color: #34D399; }

html.dark .tx-direction--out,
html.midnight .tx-direction--out { color: #F87171; }

.tx-source {
    display: flex;
    flex-direction: column;
    gap: 1px;
    line-height: 1.2;
}

.tx-source-label {
    font-weight: 600;
    font-size: 12.5px;
}

.tx-source-id {
    font-size: 11px;
    color: #8e8e93;
    font-variant-numeric: tabular-nums;
}

html.dark .tx-source-id,
html.midnight .tx-source-id { color: #9A9CAE; }

.tx-target {
    font-size: 12.5px;
}

.tx-muted {
    color: #c7c7cc;
    font-size: 12px;
}

.tx-badge {
    display: inline-flex;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 10.5px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.tx-badge--manual {
    background: rgba(99, 102, 241, 0.12);
    color: #6366f1;
}

html.dark .tx-badge--manual,
html.midnight .tx-badge--manual { color: #818CF8; }

.tx-amounts {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 130px;
    font-variant-numeric: tabular-nums;
}

.tx-amounts-total {
    font-weight: 700;
    font-size: 13.5px;
}

.tx-amounts-line2 {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 11.5px;
    color: #8e8e93;
}

.tx-amounts-paid { color: #28a745; font-weight: 600; }
.tx-amounts-sep { opacity: 0.55; }
.tx-amounts-remaining { color: #d97706; font-weight: 600; }

html.dark .tx-amounts-line2,
html.midnight .tx-amounts-line2 { color: #9A9CAE; }
html.dark .tx-amounts-paid,
html.midnight .tx-amounts-paid { color: #34D399; }
html.dark .tx-amounts-remaining,
html.midnight .tx-amounts-remaining { color: #FBBF24; }

.tx-pill {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 9px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
}

.tx-pill::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
}

.tx-pill--0 { background: rgba(107, 114, 128, 0.15); color: #6b7280; }
.tx-pill--1 { background: rgba(245, 158, 11, 0.15); color: #d97706; }
.tx-pill--2 { background: rgba(40, 167, 69, 0.15); color: #28a745; }
.tx-pill--3 { background: rgba(59, 130, 246, 0.15); color: #3b82f6; }
.tx-pill--4 { background: rgba(239, 68, 68, 0.15); color: #ef4444; }

html.dark .tx-pill--0,
html.midnight .tx-pill--0 { color: #9CA3AF; }
html.dark .tx-pill--1,
html.midnight .tx-pill--1 { color: #FBBF24; }
html.dark .tx-pill--2,
html.midnight .tx-pill--2 { color: #34D399; }
html.dark .tx-pill--3,
html.midnight .tx-pill--3 { color: #60A5FA; }
html.dark .tx-pill--4,
html.midnight .tx-pill--4 { color: #F87171; }
</style>
